'use client';

import {
  FC,
  useMemo,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { SendDocumentDropOffDialogProps } from './SendDocumentDropOffDialog.types';
import {
  Alert,
  Text,
  Dialog,
  Input,
  Button,
  ButtonGroup,
  EntitySelectorInputChips,
  Flex,
  Select,
  Checkbox,
  Textarea,
  LargeBulkSendConfirmationDialog,
} from '@/_components';
import { TAX_YEARS_WITHOUT_DEFAULT } from '@/_constants/year-options';
import { RolloverItemEditor } from './components/RolloverItemEditor';
import { AdditionalItemEditor } from './components/AdditionalItemEditor/';
import Link from 'next/link';
import { getIsPastDate, getNDaysAfter } from '@/_utils/dates';
import {
  sendQuestionnaires,
  type SendQuestionnaireParams,
} from '@/_api/admin-questionnaires';
import { type SendQuestionnaireQuestion } from '@/_constants/questionnaires';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { useEntityCount } from '@/_hooks/useEntityCount';
import { getPriorYearItems } from '@/_api/admin-prior-year-items';
import { PriorYearItem } from '@/_types/models/priorYearItem';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { AdditionalItem } from './components/AdditionalItemEditor/AdditionalItemEditor.types';

const TODAYS_DATE: string = getNDaysAfter(0);
const AFTER_7_DAYS: string = getNDaysAfter(7);
const AFTER_14_DAYS: string = getNDaysAfter(14);
const AFTER_30_DAYS: string = getNDaysAfter(30);

const FIRST_DATE: string = getNDaysAfter(1);

export const SendDocumentDropOffDialog: FC<SendDocumentDropOffDialogProps> = ({
  isOpen,
  onClose,
  initiallySelectedCompanyIds,
}) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { selectedCompany } = useContext(CompanyContext);
  const { loadQuestionnaires } = useContext(QuestionnaireContext);

  const [dueDate, setDueDate] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isLoadingRolloverItems, setIsLoadingRolloverItems] =
    useState<boolean>(true);
  const [taxYear, setTaxYear] = useState<string>('');
  const [customName, setCustomName] = useState<string>('Document Checklist');
  const [customEmailSubject, setCustomEmailSubject] = useState<string>('');
  const [customEmailMessage, setCustomEmailMessage] = useState<string>('');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Array<number>>(
    (selectedCompany && [selectedCompany.id]) ||
      initiallySelectedCompanyIds ||
      [],
  );
  const [additionalItems, setAdditionalItems] = useState<Array<string>>([]);
  const [rolloverItems, setRolloverItems] = useState<Array<PriorYearItem>>([]);
  const [bulkSelectedEntityTypes, setBulkSelectedEntityTypes] = useState<
    Array<string>
  >([]);

  const [includeAdditionalItems, setIncludeAdditionalItems] =
    useState<boolean>(false);
  const [includeRolloverItems, setIncludeRolloverItems] =
    useState<boolean>(false);

  const [hasCustomEmail, setHasCustomEmail] = useState<boolean>(false);

  const [
    shouldShowLargeBulkSendConfirmation,
    setShouldShowLargeBulkSendConfirmation,
  ] = useState<boolean>(false);
  const [shouldSendDraft, setShouldSendDraft] = useState<boolean>(false);

  const entityCountForConfirmation = useEntityCount({
    bulkSelectedEntityTypes,
    selectedCompanyIds,
  });

  const onReset = useCallback(() => {
    setAdditionalItems([]);
    setSelectedCompanyIds([]);
    setDueDate('');
    setTaxYear('');
    setIncludeAdditionalItems(false);
    setIncludeRolloverItems(false);
    setCustomName('Document Checklist');
    setCustomEmailSubject('');
    setCustomEmailMessage('');
    setBulkSelectedEntityTypes([]);
  }, []);

  useEffect(() => {
    if (selectedCompany?.id && !initiallySelectedCompanyIds?.length) {
      setSelectedCompanyIds([selectedCompany?.id]);
    }

    if (!isOpen) onReset();
  }, [isOpen, selectedCompany?.id, initiallySelectedCompanyIds, onReset]);

  useEffect(() => {
    if (!!initiallySelectedCompanyIds?.length) {
      setSelectedCompanyIds(initiallySelectedCompanyIds);
    }
  }, [initiallySelectedCompanyIds]);

  useEffect(() => {
    if (includeRolloverItems) {
      loadPriorYearItems();
    } else {
      setRolloverItems([]);
    }
  }, [includeRolloverItems, selectedCompanyIds[0], taxYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChipsChange = (chips: Array<number>) => setSelectedCompanyIds(chips);

  const onYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = event.target.value.trim();
    setTaxYear(year);
  };

  const onDueDateChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setDueDate(event.target.value);

  const onAfter7Days = () => setDueDate(AFTER_7_DAYS);
  const onAfter14Days = () => setDueDate(AFTER_14_DAYS);
  const onAfter30Days = () => setDueDate(AFTER_30_DAYS);

  const onCustomNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCustomName(event.target.value);

  const onEmailSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCustomEmailSubject(event.target.value);

  const onEmailMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => setCustomEmailMessage(event.target.value);

  const onAdditionalItemsToggle = () =>
    setIncludeAdditionalItems(!includeAdditionalItems);

  const onRolloverItemsToggle = () =>
    setIncludeRolloverItems(!includeRolloverItems);

  const onCustomEmailToggle = () => setHasCustomEmail(!hasCustomEmail);

  const onAddAdditionalItem = (newItem: string) => {
    if (!newItem) return;

    const newItems: Array<string> = [...additionalItems, newItem];
    return setAdditionalItems(newItems);
  };

  const onSaveEditAdditionalItem = (item: AdditionalItem) => {
    const newItems: Array<string> = [...additionalItems];
    newItems[item.key] = item.text;
    return setAdditionalItems(newItems);
  };

  const onRemoveAdditionalItem = (removeItem: string) => {
    if (!removeItem) return;

    const newItems: Array<string> = [...additionalItems].filter(
      (i) => i !== removeItem,
    );
    return setAdditionalItems(newItems);
  };

  const isFormValid: boolean = useMemo(() => {
    const isDueDateValid: boolean = !!dueDate
      ? getIsPastDate(TODAYS_DATE, dueDate)
      : true;

    const hasCompaniesSelected: boolean =
      !!selectedCompanyIds?.length || !!bulkSelectedEntityTypes.length;

    return hasCompaniesSelected && !!taxYear && isDueDateValid;
  }, [
    selectedCompanyIds?.length,
    bulkSelectedEntityTypes.length,
    taxYear,
    dueDate,
  ]);

  const isEmpty: boolean = useMemo(
    () =>
      (!includeAdditionalItems && !includeRolloverItems) || // if both toggles are off, it's empty, or...
      (additionalItems.length === 0 && rolloverItems.length === 0), // if both lists are empty, it's also empty
    [
      includeAdditionalItems,
      includeRolloverItems,
      additionalItems.length,
      rolloverItems.length,
    ],
  );

  const onSubmit = async (isDraft?: boolean) => {
    setShouldSendDraft(!!isDraft);

    if (selectedCompanyIds.length >= 10 || !!bulkSelectedEntityTypes?.length) {
      setShouldShowLargeBulkSendConfirmation(true);
    } else {
      performQuestionnaireSend(!!isDraft);
    }
  };

  const performQuestionnaireSend = async (isDraft: boolean) => {
    try {
      setLoading(true);

      const questionsToCreate: SendQuestionnaireParams['questions_to_create'] =
        [];

      additionalItems?.forEach((item) => {
        const newItem: SendQuestionnaireQuestion = {
          text: `Please upload ${item}`,
          options: [item],
          section_id: 0,
          question_type: 'upload',
        };

        questionsToCreate.push(newItem);
      });

      const newForm: SendQuestionnaireParams = {
        frequency: null,
        questionnaire_template_id: null,
        company_ids: selectedCompanyIds,
        company_types: bulkSelectedEntityTypes,
        tax_year: Number(taxYear),
        due_date: dueDate,
        questions_to_create: questionsToCreate,
        custom_template_name: customName,
        custom_email_subject: customEmailSubject,
        custom_email_body: customEmailMessage,
        days_until_due: 14,
        first_scheduled_date: FIRST_DATE,
        include_prior_year_items: !!includeRolloverItems,
        all_owner_signature_required: false,
        deposit_required: null,
        deposit_amount: null,
        update_profile_section: false,
        batch_sending: false,
        batch_reminder: false,
        document_popout_checklist_enabled: false,
        is_draft: isDraft,
        document_drop_off_enabled: true,
      };

      const { error } = await sendQuestionnaires({
        params: newForm,
        token,
      });

      if (!error) {
        const message: string = isDraft
          ? 'Document Drop Off successfully saved'
          : 'Document Drop Off successfully sent';
        addToast('success', message, { error });
        setTimeout(async () => {
          await loadQuestionnaires();
        }, 3000);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onClose();
      setLoading(false);
    }
  };

  const onLocalClose = () => {
    onReset();
    onClose();
  };

  const onSaveDraft = () => onSubmit(true);
  const onSend = () => onSubmit(false);

  const loadPriorYearItems = useCallback(async () => {
    if (!token || !selectedCompanyIds[0]) return;

    try {
      const rollovers = (await getPriorYearItems({
        companyId: selectedCompanyIds[0],
        token,
      })) as Array<PriorYearItem>;

      const filteredRollovers = rollovers.filter(
        (r) => r.year === Number(taxYear),
      );

      setRolloverItems(filteredRollovers);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoadingRolloverItems(false);
    }
  }, [token, addToast, selectedCompanyIds[0], taxYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMissingData: boolean = isEmpty && isFormValid;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onLocalClose}
        title="Send Document Drop Off"
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={10}>
            <Alert type="info">
              Fill out these fields to send Document Drop Off. Links expire in 7
              days. Do not ask for sensitive info like SSN, as no login is
              required. Clients can&apos;t leave notes.
            </Alert>
            <Flex gap={4}>
              <EntitySelectorInputChips
                label="Send To"
                hideDisabledEntities={true}
                onChipsChange={onChipsChange}
                onClearBulkSelection={() => setBulkSelectedEntityTypes([])}
                isBulkTypeSelectionEnabled={!selectedCompany?.id}
                setBulkSelectedEntityTypes={setBulkSelectedEntityTypes}
                bulkSelectedEntityTypes={bulkSelectedEntityTypes}
                preselectedEntityIds={selectedCompanyIds}
                isDisabled={
                  !!selectedCompany?.id || !!bulkSelectedEntityTypes?.length
                }
              />
              <Input
                label="Send As"
                onChange={onCustomNameChange}
                value={customName}
              />
              <Select
                items={TAX_YEARS_WITHOUT_DEFAULT}
                label="Tax Year"
                placeholder="Select Tax Year"
                onChange={onYearChange}
                selectedValue={taxYear}
              />
            </Flex>
            <Flex gap={4} isColumn={true}>
              <Input
                label="Due Date"
                min={TODAYS_DATE}
                type="date"
                isDisabled={isLoading}
                value={dueDate}
                onChange={onDueDateChange}
              />
              <ButtonGroup isSmallGroup={true}>
                <Button
                  value="7 days from now"
                  iconLeft={
                    dueDate === AFTER_7_DAYS ? 'check-circle-filled' : 'circle'
                  }
                  intent="secondary"
                  size="tiny"
                  onClick={onAfter7Days}
                />
                <Button
                  value="2 weeks from now"
                  iconLeft={
                    dueDate === AFTER_14_DAYS ? 'check-circle-filled' : 'circle'
                  }
                  intent="secondary"
                  size="tiny"
                  onClick={onAfter14Days}
                />
                <Button
                  value="1 month from now"
                  iconLeft={
                    dueDate === AFTER_30_DAYS ? 'check-circle-filled' : 'circle'
                  }
                  intent="secondary"
                  size="tiny"
                  onClick={onAfter30Days}
                />
              </ButtonGroup>
              <Text type="tiny" color="secondary">
                We stop sending reminders past the due date.
                <br />
                Locking a sent Document Drop Off would also prevent recurring
                reminders.
              </Text>
            </Flex>

            <Flex isColumn={true} gap={2}>
              <Checkbox
                label="Include Rollover Items"
                isChecked={includeRolloverItems}
                onChange={onRolloverItemsToggle}
              />
              <Checkbox
                label="Include Additional Items"
                isChecked={includeAdditionalItems}
                onChange={onAdditionalItemsToggle}
              />
              <Checkbox
                label="Custom Email"
                isChecked={hasCustomEmail}
                onChange={onCustomEmailToggle}
              />
            </Flex>
            {hasCustomEmail && (
              <>
                <Flex isColumn={true} gap={6}>
                  <Input
                    value={customEmailSubject}
                    onChange={onEmailSubjectChange}
                    label="Email Subject"
                  />
                  <Textarea
                    rows={5}
                    value={customEmailMessage}
                    onChange={onEmailMessageChange}
                    label="Email Message"
                  />
                </Flex>
              </>
            )}
            {includeRolloverItems &&
              (selectedCompanyIds?.length === 1 ? (
                <RolloverItemEditor
                  companyId={selectedCompanyIds[0]}
                  year={taxYear}
                  isLoading={isLoadingRolloverItems}
                  additionalItems={additionalItems}
                  rolloverItems={rolloverItems}
                  loadPriorYearItems={loadPriorYearItems}
                />
              ) : (
                <Alert type="info">
                  Rollover items can be edited in the{' '}
                  <Link href="/clients">
                    <b>Entity Dashboard</b>
                  </Link>
                  .
                </Alert>
              ))}
            {includeAdditionalItems && (
              <AdditionalItemEditor
                onRemove={onRemoveAdditionalItem}
                onCreate={onAddAdditionalItem}
                onSaveEdit={onSaveEditAdditionalItem}
                items={additionalItems}
                rolloverItems={rolloverItems}
              />
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <Flex isBetween={true}>
            <ButtonGroup align="right">
              <Button intent="secondary" value="Cancel" onClick={onClose} />
            </ButtonGroup>
            <ButtonGroup align="right">
              <Button
                intent="secondary"
                value="Save draft"
                onClick={onSaveDraft}
                isDisabled={!isFormValid || isEmpty}
                tooltip="Save as draft and send later. Your users will NOT be able to view this yet."
                withTooltip
              />
              <Button
                intent="primary"
                value="Send Document Drop Off"
                iconRight="send"
                onClick={onSend}
                isLoading={isLoading}
                isDisabled={!isFormValid || isEmpty || !dueDate}
                tooltip={
                  hasMissingData
                    ? 'Please add at least 1 prior year item or additional item'
                    : 'Please select a due date'
                }
                withTooltip={hasMissingData || !dueDate}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Footer>
      </Dialog>
      {shouldShowLargeBulkSendConfirmation && (
        <LargeBulkSendConfirmationDialog
          isOpen={shouldShowLargeBulkSendConfirmation}
          onClose={() => setShouldShowLargeBulkSendConfirmation(false)}
          bulkSelectedEntityTypes={bulkSelectedEntityTypes}
          entityCount={entityCountForConfirmation}
          onConfirm={() => {
            performQuestionnaireSend(shouldSendDraft);
            setShouldShowLargeBulkSendConfirmation(false);
          }}
          isDraft={shouldSendDraft}
        />
      )}
    </>
  );
};
